import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Seo } from '../components/Seo';

const Block = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-[#C5A880] mb-4">{title}</h2>
    <div className="space-y-2.5 text-[#EADBC8]/80 leading-relaxed text-sm md:text-base">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex items-start"><span className="text-[#C5A880] mr-2">•</span><span>{children}</span></p>
);

const BookingTerms = () => (
  <div className="min-h-screen bg-[#1F1A17]">
    <Seo
      title="Booking Terms Summary"
      description="A plain-English summary of Mayden Films booking, payment, cancellation, rescheduling and gallery retention terms. Australian Consumer Law rights are not excluded."
      path="/booking-terms"
    />
    <section className="pt-36 pb-12 bg-gradient-to-br from-[#1F1A17] via-[#2A2420] to-[#1F1A17]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-[#EADBC8] mb-4">Booking Terms Summary</h1>
        <p className="text-[#EADBC8]/60">A clear overview of how bookings work. Australian Consumer Law rights are not excluded.</p>
      </div>
    </section>

    <section className="py-16 bg-[#1A1612]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Block title="Payment & booking">
          <Li>A personalised quote is issued after your enquiry has been reviewed.</Li>
          <Li>Your date is not held by an enquiry or quote alone.</Li>
          <Li>A booking becomes confirmed only after Mayden Films receives the signed booking agreement and 30% booking fee.</Li>
          <Li>The 30% is calculated from the complete booking total, including selected upgrades and travel charges.</Li>
          <Li>The remaining balance is due 14 days before the event.</Li>
          <Li>Final selections and event details are also due 14 days before the event.</Li>
        </Block>

        <Block title="Cancellation">
          <Li>30 days or more before the event: a cancellation charge of up to the 30% booking fee, limited to Mayden Films’ reasonable administration, work completed, purchases and actual loss.</Li>
          <Li>15–29 days before the event: a cancellation charge of up to 50% of the total, limited to reasonable loss.</Li>
          <Li>14 days or less before the event: a cancellation charge of up to 100% of the total, limited to reasonable loss.</Li>
          <Li>Costs saved, recovered or offset through a replacement booking are taken into account.</Li>
          <Li>Remedies required under Australian Consumer Law remain available.</Li>
        </Block>

        <Block title="Rescheduling">
          <Li>One reschedule is permitted.</Li>
          <Li>With at least 21 days’ notice, the booking fee may transfer to the replacement date without a rescheduling fee.</Li>
          <Li>The replacement event must take place within 12 months of the original date.</Li>
          <Li>The new date is subject to availability and current package pricing.</Li>
          <Li>With 8–20 days’ notice, a $100 rescheduling fee applies, plus unrecoverable costs.</Li>
          <Li>With seven days or less notice, the cancellation policy applies, although genuine emergencies may be considered at Mayden Films’ discretion.</Li>
          <Li>A second reschedule is treated as a cancellation.</Li>
          <Li>Travel and other charges are recalculated for the new date and venue.</Li>
        </Block>

        <Block title="If Mayden Films cannot attend">
          <p>If Mayden Films cannot provide the booked service because of illness, emergency or equipment failure, appropriate options may include a replacement date, a mutually agreed replacement provider where practical, or a refund for services not supplied — subject to Australian Consumer Law.</p>
        </Block>

        <Block title="Gallery & file retention">
          <Li>The online event gallery remains available for 60 days after delivery.</Li>
          <Li>You are responsible for downloading and backing up your files.</Li>
          <Li>A 30-day gallery extension costs $25 when requested before expiry.</Li>
          <Li>Mayden Films normally keeps a working backup for 90 days from the event date.</Li>
          <Li>Files may be permanently deleted after 90 days.</Li>
          <Li>Retrieval after gallery expiry costs $75, but only if the files remain available.</Li>
          <Li>Mayden Films does not guarantee permanent archiving or recovery.</Li>
        </Block>

        <div className="mt-12 text-center">
          <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6">Request a Quote</Button></Link>
        </div>
      </div>
    </section>
  </div>
);

export default BookingTerms;
